import React, { useState, useEffect, useCallback, memo } from 'react';
import { BaseEdge, EdgeProps, getSmoothStepPath, EdgeLabelRenderer, Position } from '@xyflow/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Loader2, ArrowRight, Network, Clock, Activity, ShieldCheck, ShieldX, AlertTriangle, Brain, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { apiClient, FlowSummary, InventoryCluster } from '../../api/client';
import {
    EnrichedFlowSummary,
    BidirectionalFlowData,
    EnrichedBidirectionalFlowData,
    isCloudResourceKind,
} from './types';

// --- CLICKABLE FLOW EDGE ---
export const ClickableFlowEdge = memo(({
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    style,
    markerEnd,
    data
}: EdgeProps) => {
    const [isHovered, setIsHovered] = useState(false);

    const flowType = (data as any)?.flowType;

    // When nodes are at nearly the same Y level, getSmoothStepPath collapses
    // to a straight horizontal line — all parallel edges overlap. Build a
    // manual right-angle step path that routes through a unique vertical
    // offset so each edge is visually distinguishable.
    const idHash = id.split('').reduce((acc, ch) => ((acc << 5) - acc + ch.charCodeAt(0)) | 0, 0);
    const yDiff = Math.abs(sourceY - targetY);
    const xDist = Math.abs(targetX - sourceX);
    // Only use the snake path when nodes are far enough apart that there are
    // likely containers between them. Adjacent nodes (short xDist) get a
    // normal straight/smoothstep edge — no snake routing needed.
    const needsSnakePath = yDiff < 30 && xDist > 300;

    let edgePath: string;
    let labelX: number;
    let labelY: number;

    if (needsSnakePath) {
        // Route AROUND containers with horizontal lead-in/out from handles.
        // Lead directions are based on actual handle positions so segments go
        // OUT of the source node and INTO the target node, never through them.
        const slots = [-55, -70, -85, 60, 75];
        const slot = ((idHash % slots.length) + slots.length) % slots.length;
        const vOffset = slots[slot];

        const midY = (sourceY + targetY) / 2 + vOffset;
        const srcDir = sourcePosition === Position.Left ? -1 : 1;
        const tgtDir = targetPosition === Position.Left ? -1 : 1;
        const gap = 18;
        const x1 = sourceX + srcDir * gap;
        const x2 = targetX + tgtDir * gap;
        const tgtInDir = -tgtDir;
        const midHDir = Math.sign(x2 - x1) || 1;
        const vDir1 = Math.sign(midY - sourceY) || -1;
        const vDir2 = Math.sign(targetY - midY) || 1;
        const r = 8;

        edgePath = [
            `M ${sourceX},${sourceY}`,
            `L ${x1 - srcDir * r},${sourceY}`,
            `Q ${x1},${sourceY} ${x1},${sourceY + vDir1 * r}`,
            `L ${x1},${midY - vDir1 * r}`,
            `Q ${x1},${midY} ${x1 + midHDir * r},${midY}`,
            `L ${x2 - midHDir * r},${midY}`,
            `Q ${x2},${midY} ${x2},${midY + vDir2 * r}`,
            `L ${x2},${targetY - vDir2 * r}`,
            `Q ${x2},${targetY} ${x2 + tgtInDir * r},${targetY}`,
            `L ${targetX},${targetY}`,
        ].join(' ');

        labelX = (x1 + x2) / 2;
        labelY = midY;
    } else {
        const baseOffset = flowType === 'l7' ? 40 : 20;
        const edgeOffset = baseOffset + (((idHash % 5) + 5) % 5) * 10;

        [edgePath, labelX, labelY] = getSmoothStepPath({
            sourceX,
            sourceY,
            sourcePosition,
            targetX,
            targetY,
            targetPosition,
            borderRadius: 8,
            offset: edgeOffset,
        });
    }

    const handleClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        if (data?.onEdgeClick) {
            (data as any).onEdgeClick((data as any).payload);
        }
    };

    const strokeColor = style?.stroke || '#888';
    const flowCount = (data as any)?.count || 1;
    const srcName = (data as any)?.srcName || 'unknown';
    const dstName = (data as any)?.dstName || 'unknown';
    const protocol = (data as any)?.protocol || '';
    const port = (data as any)?.port || '';

    // Highlight color - cyan stands out against red/green/yellow
    const highlightColor = '#00D4FF';

    // Enhanced styles when hovered - change to cyan highlight
    const hoveredStyle = isHovered ? {
        ...style,
        stroke: highlightColor,
        strokeWidth: 4,
        filter: `drop-shadow(0 0 8px ${highlightColor})`,
    } : style;

    // Use highlight color for dot when hovered
    const dotColor = isHovered ? highlightColor : strokeColor;

    return (
        <>
            {/* Invisible wider path for easier hover detection AND click handling */}
            <path
                d={edgePath}
                fill="none"
                stroke="transparent"
                strokeWidth={20}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onClick={handleClick}
                style={{ cursor: 'pointer' }}
            />
            {/* Actual visible edge */}
            <BaseEdge
                id={id}
                path={edgePath}
                style={hoveredStyle}
                markerEnd={markerEnd}
            />
            <EdgeLabelRenderer>
                {/* Hover tooltip showing flow details */}
                {isHovered && (
                    <div
                        style={{
                            position: 'absolute',
                            transform: `translate(-50%, -150%) translate(${labelX}px,${labelY}px)`,
                            pointerEvents: 'none',
                            zIndex: 1001,
                        }}
                        className="nodrag nopan"
                    >
                        <div
                            className="bg-popover text-popover-foreground border rounded-md shadow-lg px-3 py-2 text-xs whitespace-nowrap"
                            style={{ minWidth: 150 }}
                        >
                            <div className="font-semibold mb-1">
                                {srcName} → {dstName}
                            </div>
                            <div className="text-muted-foreground">
                                {protocol && <span>{protocol}</span>}
                                {port && <span>:{port}</span>}
                                {flowCount > 1 && <span className="ml-2">({flowCount} flows)</span>}
                            </div>
                        </div>
                    </div>
                )}
                {/* Clickable dot */}
                <div
                    style={{
                        position: 'absolute',
                        transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
                        pointerEvents: 'all',
                        zIndex: isHovered ? 1002 : 1000,
                    }}
                    className="nodrag nopan"
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    <button
                        onClick={handleClick}
                        className="transition-all duration-150"
                        style={{
                            width: isHovered ? 24 : 18,
                            height: isHovered ? 24 : 18,
                            background: 'var(--background, white)',
                            border: `2px solid ${dotColor}`,
                            borderRadius: '50%',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0,
                            boxShadow: isHovered ? `0 0 12px ${highlightColor}` : 'none',
                        }}
                    >
                        <span
                            style={{
                                width: isHovered ? 10 : 8,
                                height: isHovered ? 10 : 8,
                                borderRadius: '50%',
                                backgroundColor: dotColor,
                            }}
                        />
                    </button>
                </div>
            </EdgeLabelRenderer>
        </>
    );
});

// --- FLOW SUMMARY ANALYSIS ---
// Component to display flow information with structured layout matching external flow format
export const FlowSummaryAnalysis: React.FC<{
    flows: FlowSummary[];
    startIndex?: number;
    onLoadSuspiciousReasons?: (flow: FlowSummary) => void;
}> = ({ flows, startIndex = 0, onLoadSuspiciousReasons }) => {
    const formatTimestamp = (timestamp: number) => {
        if (!timestamp) return 'N/A';
        // Handle both seconds and milliseconds timestamps
        // If timestamp is greater than 10 billion, it's already in milliseconds
        const ms = timestamp > 10000000000 ? timestamp : timestamp * 1000;
        return new Date(ms).toLocaleString();
    };

    // Determine endpoint type for display
    // Order matters: check cloud resources first (they may have empty namespace)
    const getEndpointType = (kind: string, namespace: string, ip?: string): { label: string; isExternal: boolean; isCloud: boolean } => {
        // Check cloud resource types first - they may have empty namespace but aren't external
        if (isCloudResourceKind(kind)) {
            return { label: 'Cloud', isExternal: false, isCloud: true };
        }

        // External/unmanaged types (only if not a cloud resource)
        // Also check for IP address which indicates external/cloud endpoints
        if (kind === 'Unmanaged' || kind === 'External' || namespace === 'external' || namespace === '') {
            return { label: 'External', isExternal: true, isCloud: false };
        }

        // If there's an IP address but no namespace, treat as external
        if (ip && !namespace) {
            return { label: 'External', isExternal: true, isCloud: false };
        }

        // K8s in-cluster
        return { label: 'In-Cluster', isExternal: false, isCloud: false };
    };

    // Get dot color based on endpoint type
    const getEndpointDotColor = (kind: string, namespace: string, ip?: string): string => {
        const { isExternal, isCloud } = getEndpointType(kind, namespace, ip);
        if (isExternal) return 'bg-orange-500';
        if (isCloud) return 'bg-amber-500';
        return 'bg-blue-500';
    };

    if (!flows || flows.length === 0) {
        return <div className="text-sm text-muted-foreground">No flows available.</div>;
    }

    return (
        <div className="space-y-4">
            <div className="space-y-4 max-h-96 overflow-y-auto">
                {flows.map((flow, index) => {

                    // Simple priority: yellow (suspicious) > red (denied) > green (allowed)
                    let borderColor: string;
                    const hasSuspicious = (flow.suspicious_count || 0) > 0 || flow.suspicious;
                    const hasDenied = (flow.denied_count || 0) > 0;

                    if (hasSuspicious) {
                        borderColor = '#f59e0b'; // Yellow for suspicious (highest priority)
                    } else if (hasDenied || !flow.allowed) {
                        borderColor = '#ef4444'; // Red for denied (medium priority)
                    } else {
                        borderColor = '#10b981'; // Green for allowed (lowest priority)
                    }

                    return (
                        <Card key={index} className="border-l-4" style={{borderLeftColor: borderColor}}>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Network className="h-5 w-5" />
                                    Flow {startIndex + index + 1}
                                </CardTitle>
                            </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Flow Direction with Arrow */}
                            <div className="flex items-center justify-between bg-muted/30 p-3 rounded-lg">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className={`w-2 h-2 rounded-full ${flow.src_known_ip_name ? 'bg-zinc-500' : getEndpointDotColor(flow.src_kind, flow.src_namespace, flow.src_ip)}`} />
                                        <span className="font-medium text-sm">
                                            {flow.src_known_ip_name ? 'Known IP' : getEndpointType(flow.src_kind, flow.src_namespace, flow.src_ip).label}
                                        </span>
                                    </div>
                                    <div className="text-sm font-medium break-all">
                                        {flow.src_known_ip_name ? (
                                            <div>
                                                <span className="text-zinc-600 dark:text-zinc-400">{flow.src_known_ip_name}</span>
                                                <div className="text-xs text-muted-foreground">{flow.src_ip || flow.src_name}</div>
                                            </div>
                                        ) : (() => {
                                            const { isExternal, isCloud } = getEndpointType(flow.src_kind, flow.src_namespace, flow.src_ip);
                                            if (isExternal) {
                                                return flow.src_name;
                                            }
                                            if (isCloud) {
                                                // For cloud resources, just show the name without the long tenancy OCID
                                                return flow.src_name;
                                            }
                                            // K8s in-cluster: show namespace/name
                                            return flow.src_name
                                                ? flow.src_namespace
                                                    ? `${flow.src_namespace}/${flow.src_name}`
                                                    : flow.src_name
                                                : flow.src_namespace;
                                        })()
                                        }
                                    </div>
                                    {!flow.src_known_ip_name && <div className="text-xs text-muted-foreground">{flow.src_kind}</div>}
                                </div>

                                <div className="flex items-center px-4">
                                    {flow.src_kind === 'Namespace' && flow.dst_kind === 'Namespace' ? (
                                        <span className="text-lg font-bold text-muted-foreground">↔</span>
                                    ) : (
                                        <ArrowRight className="h-6 w-6 text-muted-foreground" />
                                    )}
                                </div>

                                <div className="flex-1 text-right">
                                    <div className="flex items-center justify-end gap-2 mb-1">
                                        <span className="font-medium text-sm">
                                            {flow.dst_known_ip_name ? 'Known IP' : getEndpointType(flow.dst_kind, flow.dst_namespace, flow.dst_ip).label}
                                        </span>
                                        <div className={`w-2 h-2 rounded-full ${flow.dst_known_ip_name ? 'bg-zinc-500' : getEndpointDotColor(flow.dst_kind, flow.dst_namespace, flow.dst_ip)}`} />
                                    </div>
                                    <div className="text-sm font-medium break-all">
                                        {flow.dst_known_ip_name ? (
                                            <div>
                                                <span className="text-zinc-600 dark:text-zinc-400">{flow.dst_known_ip_name}</span>
                                                <div className="text-xs text-muted-foreground">{flow.dst_ip || flow.dst_name}</div>
                                            </div>
                                        ) : (() => {
                                            const { isExternal, isCloud } = getEndpointType(flow.dst_kind, flow.dst_namespace, flow.dst_ip);
                                            if (isExternal) {
                                                return flow.dst_name;
                                            }
                                            if (isCloud) {
                                                // For cloud resources, just show the name without the long tenancy OCID
                                                return flow.dst_name;
                                            }
                                            // K8s in-cluster: show namespace/name
                                            return flow.dst_name
                                                ? flow.dst_namespace
                                                    ? `${flow.dst_namespace}/${flow.dst_name}`
                                                    : flow.dst_name
                                                : flow.dst_namespace;
                                        })()
                                        }
                                    </div>
                                    {!flow.dst_known_ip_name && <div className="text-xs text-muted-foreground">{flow.dst_kind}</div>}
                                </div>
                            </div>

                            {/* HTTP Method and Path (if available) */}
                            {(flow as any).method && (
                                <div className="bg-muted/30 p-3 rounded-lg">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="space-y-1">
                                            <div className="text-sm font-medium text-muted-foreground">Method</div>
                                            <div className="text-sm font-semibold">{(flow as any).method}</div>
                                        </div>
                                        <div className="space-y-1">
                                            <div className="text-sm font-medium text-muted-foreground">Path</div>
                                            <div className="text-sm font-semibold">{(flow as any).path || '/'}</div>
                                        </div>
                                        {(flow as any).response_code && (
                                            <div className="space-y-1">
                                                <div className="text-sm font-medium text-muted-foreground">Response Code</div>
                                                <div className="text-sm font-semibold">{(flow as any).response_code}</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}


                            {/* Flow Details Grid */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="space-y-1">
                                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                                        <Network className="h-4 w-4" />
                                        Protocol{(flow.protocols?.length || 0) > 1 ? 's' : ''}
                                    </div>
                                    <div className="text-sm font-semibold">
                                        {(() => {
                                            // Handle protocols array from the backend aggregation
                                            const protocols = flow.protocols;
                                            if (protocols && protocols.length > 0) {
                                                const displayProtocols = protocols.map((p: string) => {
                                                    if (p === 'L7_PROTOCOL_HTTP') return 'HTTP';
                                                    if (p === 'L7_PROTOCOL_TCP') return 'TCP';
                                                    return p.toUpperCase();
                                                });
                                                if (protocols.length <= 3) {
                                                    return displayProtocols.join(', ');
                                                } else {
                                                    return `${displayProtocols.slice(0, 2).join(', ')}, +${protocols.length - 2} more`;
                                                }
                                            }

                                            // Fallback to single protocol
                                            const protocol = flow.protocol?.toUpperCase() || 'Unknown';
                                            // Extract just the protocol part if it contains method/path
                                            if (protocol.includes(' ')) {
                                                return protocol.split(' ')[0];
                                            }
                                            return protocol;
                                        })()}
                                    </div>
                                </div>

                                <div className="space-y-1">
                                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                                        <Activity className="h-4 w-4" />
                                        Port{(flow.ports?.length || 0) > 1 ? 's' : ''}
                                    </div>
                                    <div className="text-sm font-semibold">
                                        {(() => {
                                            const ports = flow.ports;
                                            if (ports && ports.length > 0) {
                                                if (ports.length <= 3) {
                                                    return ports.join(', ');
                                                } else {
                                                    return `${ports.slice(0, 3).join(', ')}, +${ports.length - 3} more`;
                                                }
                                            }
                                            return flow.port || 'Unknown';
                                        })()}
                                    </div>
                                </div>

                                <div className="space-y-1">
                                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                                        <Activity className="h-4 w-4" />
                                        Count
                                    </div>
                                    <div className="text-sm font-semibold">
                                        {flow.count.toLocaleString()}
                                    </div>
                                </div>

                                <div className="space-y-1">
                                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                                        {!flow.allowed ? <ShieldX className="h-4 w-4" /> :
                                         flow.suspicious ? <AlertTriangle className="h-4 w-4" /> :
                                         <ShieldCheck className="h-4 w-4" />}
                                        Status
                                    </div>
                                    <div className="flex flex-wrap gap-1">
                                        {(() => {
                                            const statusBadges = [];
                                            const allowedCount = flow.allowed_count || 0;
                                            const deniedCount = flow.denied_count || 0;
                                            const suspiciousCount = flow.suspicious_count || 0;
                                            const totalCount = flow.count || 0;

                                            // Calculate non-suspicious allowed/denied counts
                                            const nonSuspiciousAllowed = Math.max(0, allowedCount - suspiciousCount);
                                            const nonSuspiciousDenied = Math.max(0, deniedCount - suspiciousCount);

                                            // Show non-suspicious counts first
                                            if (nonSuspiciousAllowed > 0) {
                                                statusBadges.push(
                                                    <Badge key="allowed" className="text-xs bg-green-100 text-green-800 border-green-300 dark:bg-green-950/30 dark:text-green-200 dark:border-green-700">
                                                        Allowed ({nonSuspiciousAllowed})
                                                    </Badge>
                                                );
                                            }
                                            if (nonSuspiciousDenied > 0) {
                                                statusBadges.push(
                                                    <Badge key="denied" className="text-xs bg-red-100 text-red-800 border-red-300 dark:bg-red-950/30 dark:text-red-200 dark:border-red-700">
                                                        Denied ({nonSuspiciousDenied})
                                                    </Badge>
                                                );
                                            }

                                            // Show suspicious count (these can be allowed OR denied)
                                            if (suspiciousCount > 0 || flow.suspicious) {
                                                statusBadges.push(
                                                    <Badge key="suspicious" className="text-xs bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-950/30 dark:text-yellow-200 dark:border-yellow-700">
                                                        Suspicious ({suspiciousCount || totalCount})
                                                    </Badge>
                                                );
                                            }

                                            // Fallback for flows without count breakdown
                                            if (statusBadges.length === 0) {
                                                if (flow.suspicious) {
                                                    statusBadges.push(
                                                        <Badge key="suspicious-fallback" className="text-xs bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-950/30 dark:text-yellow-200 dark:border-yellow-700">
                                                            Suspicious ({totalCount})
                                                        </Badge>
                                                    );
                                                } else if (flow.allowed) {
                                                    statusBadges.push(
                                                        <Badge key="allowed-fallback" className="text-xs bg-green-100 text-green-800 border-green-300 dark:bg-green-950/30 dark:text-green-200 dark:border-green-700">
                                                            Allowed ({totalCount})
                                                        </Badge>
                                                    );
                                                } else {
                                                    statusBadges.push(
                                                        <Badge key="denied-fallback" className="text-xs bg-red-100 text-red-800 border-red-300 dark:bg-red-950/30 dark:text-red-200 dark:border-red-700">
                                                            Denied ({totalCount})
                                                        </Badge>
                                                    );
                                                }
                                            }

                                            return statusBadges;
                                        })()}
                                    </div>
                                </div>
                            </div>

                            {/* Timestamps and Suspicious Analysis */}
                            <div className="space-y-3 pt-2 border-t">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                                            <Clock className="h-4 w-4" />
                                            First Seen
                                        </div>
                                        <div className="text-xs">{formatTimestamp(flow.first_seen)}</div>
                                    </div>

                                    <div className="space-y-1">
                                        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                                            <Clock className="h-4 w-4" />
                                            Last Seen
                                        </div>
                                        <div className="text-xs">{formatTimestamp(flow.last_seen)}</div>
                                    </div>
                                </div>

                                {/* Suspicious flow analysis button - only for workload-level flows */}
                                {flow.suspicious && flow.src_kind !== 'Namespace' && flow.dst_kind !== 'Namespace' && (
                                    <div className="flex justify-center pt-2 border-t border-amber-200 dark:border-amber-800">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                if (onLoadSuspiciousReasons) {
                                                    onLoadSuspiciousReasons(flow);
                                                }
                                            }}
                                            className="text-xs bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100 dark:bg-amber-950/30 dark:border-amber-700 dark:text-amber-200 dark:hover:bg-amber-950/50"
                                        >
                                            <Brain className="h-3 w-3 mr-1" />
                                            Why is this flow suspicious?
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                    );
                })}
            </div>
        </div>
    );
};

// --- HELPER: Normalize endpoint name (pod → workload) ---
// Pod names follow pattern: {workload-name}-{replicaset-hash}-{pod-id}
// This extracts the workload name from pod names
function normalizeEndpointName(name: string, kind: string): string {
    if (!name) return name;

    // If it's a Pod, try to extract the workload name
    if (kind === 'Pod' && name.includes('-')) {
        // Split by dash and try to identify the replicaset hash
        // Pattern: deployment-name-{rs-hash}-{pod-id}
        // The rs-hash is typically 8-10 alphanumeric characters
        // The pod-id is typically 5 alphanumeric characters
        const parts = name.split('-');
        if (parts.length >= 3) {
            // Check if last two parts look like hash-id (typically short alphanumeric)
            const lastPart = parts[parts.length - 1];
            const secondLastPart = parts[parts.length - 2];

            // If both look like hashes (short alphanumeric), strip them
            if (lastPart.length <= 6 && secondLastPart.length <= 12 &&
                /^[a-z0-9]+$/.test(lastPart) && /^[a-z0-9]+$/.test(secondLastPart)) {
                return parts.slice(0, -2).join('-');
            }
        }
    }

    return name;
}

// --- HELPER: Deduplicate flows by normalizing endpoint names ---
// Merges flows that represent the same logical workload-to-workload connection
function deduplicateFlows(flows: FlowSummary[]): FlowSummary[] {
    if (!flows || flows.length === 0) return flows;

    const flowMap = new Map<string, FlowSummary>();

    flows.forEach(flow => {
        // Normalize both endpoints
        const normalizedSrcName = normalizeEndpointName(flow.src_name, flow.src_kind);
        const normalizedDstName = normalizeEndpointName(flow.dst_name, flow.dst_kind);

        // Create a canonical key (sorted endpoints for bidirectional matching)
        // Include protocol and port for unique flow identification
        const srcKey = `${flow.src_namespace}/${normalizedSrcName}`;
        const dstKey = `${flow.dst_namespace}/${normalizedDstName}`;

        // Use sorted order to merge bidirectional flows
        const [first, second] = [srcKey, dstKey].sort();
        const flowKey = `${first}|${second}|${flow.protocol || 'TCP'}|${flow.port || 0}`;

        const existing = flowMap.get(flowKey);
        if (existing) {
            // Merge with existing flow
            existing.count = (existing.count || 1) + (flow.count || 1);
            existing.allowed_count = (existing.allowed_count || 0) + (flow.allowed_count || 0);
            existing.denied_count = (existing.denied_count || 0) + (flow.denied_count || 0);
            existing.suspicious_count = (existing.suspicious_count || 0) + (flow.suspicious_count || 0);
            existing.first_seen = Math.min(existing.first_seen || Infinity, flow.first_seen || Infinity);
            existing.last_seen = Math.max(existing.last_seen || 0, flow.last_seen || 0);
            existing.allowed = existing.allowed || flow.allowed;
            existing.suspicious = existing.suspicious || flow.suspicious;

            // Merge protocols and ports arrays if present
            if (flow.protocols) {
                const protocolSet = new Set([...(existing.protocols || []), ...flow.protocols]);
                existing.protocols = Array.from(protocolSet);
            }
            if (flow.ports) {
                const portSet = new Set([...(existing.ports || []), ...flow.ports]);
                existing.ports = Array.from(portSet);
            }
        } else {
            // Create new entry with normalized names (preserve original kinds for downstream logic)
            flowMap.set(flowKey, {
                ...flow,
                src_name: normalizedSrcName,
                dst_name: normalizedDstName,
            });
        }
    });

    return Array.from(flowMap.values());
}

// --- PROCESS FLOWS FOR DISPLAY ---
// Unified flow processing function to handle all flow display scenarios consistently
export const processFlowsForDisplay = (
    flow: FlowSummary | BidirectionalFlowData | EnrichedBidirectionalFlowData,
    isLoading: boolean
): { flows: FlowSummary[]; title: string; isLoading: boolean } => {
    const isBi = 'reverseFlow' in flow;

    if (isBi) {
        const flowAsBi = flow as BidirectionalFlowData;
        const forwardFlow = flowAsBi.flow;
        const reverseFlow = flowAsBi.reverseFlow;

        const isExternal = forwardFlow.dst_kind === 'Unmanaged' || forwardFlow.src_kind === 'Unmanaged' ||
                          reverseFlow.dst_kind === 'Unmanaged' || reverseFlow.src_kind === 'Unmanaged';

        if (isExternal) {
            // For external flows, only show individual flows if available
            const enhancedFlow = flowAsBi as EnrichedBidirectionalFlowData;
            const allIndividualFlows = [
                ...(enhancedFlow.originalFlows || []),
                ...(enhancedFlow.originalReverseFlows || [])
            ];
            return {
                flows: deduplicateFlows(allIndividualFlows as FlowSummary[]),
                title: `${forwardFlow.src_namespace} ↔ ${forwardFlow.dst_namespace}`,
                isLoading
            };
        }

        const isNamespaceToNamespace = forwardFlow.src_kind === 'Namespace' && forwardFlow.dst_kind === 'Namespace';

        if (isNamespaceToNamespace) {
            if (isLoading) {
                return {
                    flows: [],
                    title: `${forwardFlow.src_namespace} ↔ ${forwardFlow.dst_namespace}`,
                    isLoading: true
                };
            }

            // For namespace-to-namespace flows, show the aggregated summary instead of individual flows
            // Use the already-aggregated data from the backend (which includes protocols and ports arrays)
            const aggregatedFlow: FlowSummary = {
                src_namespace: forwardFlow.src_namespace,
                src_kind: 'Namespace',
                src_name: forwardFlow.src_namespace,
                dst_namespace: forwardFlow.dst_namespace,
                dst_kind: 'Namespace',
                dst_name: forwardFlow.dst_namespace,
                protocol: forwardFlow.protocol,
                port: forwardFlow.port,
                allowed: forwardFlow.allowed || reverseFlow.allowed,
                count: forwardFlow.count + reverseFlow.count,
                first_seen: Math.min(forwardFlow.first_seen || 0, reverseFlow.first_seen || 0),
                last_seen: Math.max(forwardFlow.last_seen || 0, reverseFlow.last_seen || 0),
                suspicious: (forwardFlow as any).suspicious || (reverseFlow as any).suspicious || false,
                // Include the aggregated protocols and ports from the backend
                protocols: (forwardFlow as any).protocols || [],
                ports: (forwardFlow as any).ports || [],
                // Include the aggregated count fields
                allowed_count: ((forwardFlow as any).allowed_count || 0) + ((reverseFlow as any).allowed_count || 0),
                denied_count: ((forwardFlow as any).denied_count || 0) + ((reverseFlow as any).denied_count || 0),
                suspicious_count: ((forwardFlow as any).suspicious_count || 0) + ((reverseFlow as any).suspicious_count || 0)
            };

            return {
                flows: [aggregatedFlow],
                title: `${forwardFlow.src_namespace} ↔ ${forwardFlow.dst_namespace}`,
                isLoading: false
            };
        }

        // For workload-to-workload bidirectional flows, only show individual flows if available
        const enhancedFlow = flowAsBi as EnrichedBidirectionalFlowData;
        const allIndividualFlows = [
            ...(enhancedFlow.originalFlows || []),
            ...(enhancedFlow.originalReverseFlows || [])
        ];
        return {
            flows: deduplicateFlows(allIndividualFlows as FlowSummary[]),
            title: `${forwardFlow.src_namespace} ↔ ${forwardFlow.dst_namespace}`,
            isLoading: false
        };
    } else {
        // Unidirectional flow
        const flowAsSummary = flow as FlowSummary;
        const isExternal = flowAsSummary.dst_kind === 'Unmanaged' || flowAsSummary.src_kind === 'Unmanaged';
        const isNamespaceToNamespace = flowAsSummary.src_kind === 'Namespace' && flowAsSummary.dst_kind === 'Namespace';
        const enhancedFlow = flowAsSummary as EnrichedFlowSummary;
        const hasIndividualFlows = enhancedFlow.originalFlows && enhancedFlow.originalFlows.length > 0;

        if (isExternal) {
            // For external flows, only show individual flows if available
            return {
                flows: hasIndividualFlows ? deduplicateFlows(enhancedFlow.originalFlows!) : [],
                title: `${flowAsSummary.src_namespace} ↔ ${flowAsSummary.dst_namespace}`,
                isLoading: false
            };
        }

        if (isNamespaceToNamespace) {
            if (isLoading) {
                return {
                    flows: [],
                    title: `${flowAsSummary.src_namespace} ↔ ${flowAsSummary.dst_namespace}`,
                    isLoading: true
                };
            }

            // For namespace-to-namespace flows, show the aggregated summary instead of individual flows
            // Use the already-aggregated data from the backend (which includes protocols and ports arrays)
            const aggregatedFlow: FlowSummary = {
                src_namespace: flowAsSummary.src_namespace,
                src_kind: 'Namespace',
                src_name: flowAsSummary.src_namespace,
                dst_namespace: flowAsSummary.dst_namespace,
                dst_kind: 'Namespace',
                dst_name: flowAsSummary.dst_namespace,
                protocol: flowAsSummary.protocol,
                port: flowAsSummary.port,
                allowed: flowAsSummary.allowed,
                count: flowAsSummary.count,
                first_seen: flowAsSummary.first_seen,
                last_seen: flowAsSummary.last_seen,
                suspicious: flowAsSummary.suspicious || false,
                // Include the aggregated protocols and ports from the backend
                protocols: flowAsSummary.protocols || [],
                ports: flowAsSummary.ports || [],
                // Include aggregated counts for mixed status
                allowed_count: flowAsSummary.allowed_count,
                denied_count: flowAsSummary.denied_count,
                suspicious_count: flowAsSummary.suspicious_count,
            };

            return {
                flows: [aggregatedFlow],
                title: `${flowAsSummary.src_namespace} ↔ ${flowAsSummary.dst_namespace}`,
                isLoading: false
            };
        }

        // Only show individual workload flows, never the aggregated summary
        // Deduplicate to merge flows from different perspectives (pod→deployment and deployment→pod)
        const flowsToShow = hasIndividualFlows ? deduplicateFlows(enhancedFlow.originalFlows!) : [];
        return {
            flows: flowsToShow,
            title: `${flowAsSummary.src_namespace} → ${flowAsSummary.dst_namespace}`,
            isLoading: false
        };
    }
};

// --- FLOW DETAILS DIALOG ---
export const FlowDetailsDialog: React.FC<{
    flow: (FlowSummary | BidirectionalFlowData | EnrichedBidirectionalFlowData) | null;
    onClose: () => void;
    isLoadingIndividualFlows?: boolean;
    selectedCluster?: InventoryCluster;
}> = ({ flow, onClose, isLoadingIndividualFlows = false, selectedCluster }) => {
    const [suspiciousFlowReasons, setSuspiciousFlowReasons] = useState<{flow: any, reasons: string[]} | null>(null);

    // Clear suspicious flow reasons when flow changes
    useEffect(() => {
        setSuspiciousFlowReasons(null);
    }, [flow]);

    // Function to load suspicious flow reasons (moved before early return)
    const loadSuspiciousFlowReasons = useCallback(async (suspiciousFlow: FlowSummary) => {
        try {
            const clusterUID = selectedCluster?.uid;
            if (!clusterUID) {
                console.error('No cluster UID available for suspicious flow reasons');
                return;
            }

            const reasonsResponse = await apiClient.getSuspiciousFlowReasons({
                cluster_id: clusterUID,
                src_namespace: suspiciousFlow.src_namespace,
                src_name: suspiciousFlow.src_name,
                dst_namespace: suspiciousFlow.dst_namespace,
                dst_name: suspiciousFlow.dst_name,
                port: suspiciousFlow.port.toString(),
                protocol: suspiciousFlow.protocol
            });

            setSuspiciousFlowReasons(reasonsResponse);
        } catch (error) {
            console.error('Failed to load suspicious flow reasons:', error);
        }
    }, [selectedCluster]);

    if (!flow) return null;

    // Use unified flow processing with loading support for cluster-level flows
    const { flows, title, isLoading } = processFlowsForDisplay(flow, isLoadingIndividualFlows);
    const flowCount = flows.length;



    return (
        <Dialog open={!!flow} onOpenChange={(isOpen) => !isOpen && onClose()}>
            <DialogContent className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle>
                        {isLoading ? "Loading Flow Details..." : `Unique Flow Count: ${flowCount}`}
                    </DialogTitle>
                </DialogHeader>
                {isLoading ? (
                    <div className="space-y-4">
                        <h3 className="font-semibold text-lg">{title}</h3>
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-primary" />
                            <span className="ml-2 text-sm text-muted-foreground">Loading detailed flow information...</span>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-6">

                        {/* Suspicious flow reasons display */}
                        {suspiciousFlowReasons && (
                            <Alert className="border-red-300 bg-red-50 dark:bg-red-950/20">
                                <Brain className="h-4 w-4 text-red-600" />
                                <AlertTitle className="text-red-800 dark:text-red-200 flex items-center justify-between">
                                    <span>AI Security Analysis</span>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => setSuspiciousFlowReasons(null)}
                                        className="h-6 w-6 -mr-2"
                                    >
                                        <X className="h-3 w-3" />
                                    </Button>
                                </AlertTitle>
                                <AlertDescription>
                                    <div className="mt-3 max-h-96 overflow-y-auto pr-2">
                                        <div className="space-y-3">
                                            {suspiciousFlowReasons?.reasons?.length ? suspiciousFlowReasons.reasons.map((reason, index) => (
                                                <Card key={index} className="p-3 bg-background border-l-4 border-l-red-500 hover:bg-muted/30 transition-colors">
                                                    <div className="flex items-start gap-2">
                                                        <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                                                        <p className="text-sm leading-relaxed">{reason}</p>
                                                    </div>
                                                    {suspiciousFlowReasons.reasons.length > 1 && (
                                                        <div className="mt-2 pt-2 border-t border-muted">
                                                            <span className="text-xs text-muted-foreground">
                                                                Finding #{index + 1} of {suspiciousFlowReasons.reasons.length}
                                                            </span>
                                                        </div>
                                                    )}
                                                </Card>
                                            )) : (
                                                <Card className="p-3 bg-background border-l-4 border-l-yellow-500">
                                                    <div className="flex items-center gap-2">
                                                        <AlertTriangle className="h-4 w-4 text-yellow-500" />
                                                        <p className="text-sm">No specific reasons available for this suspicious flow.</p>
                                                    </div>
                                                </Card>
                                            )}
                                        </div>
                                        {suspiciousFlowReasons?.reasons?.length > 3 && (
                                            <div className="mt-3 text-xs text-muted-foreground text-center py-2 border-t">
                                                Showing {suspiciousFlowReasons.reasons.length} suspicious flow reason{suspiciousFlowReasons.reasons.length !== 1 ? 's' : ''}
                                            </div>
                                        )}
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-3 flex items-center">
                                        <AlertTriangle className="h-3 w-3 mr-1" />
                                        Review this flow carefully and consider implementing appropriate security policies
                                    </p>
                                </AlertDescription>
                            </Alert>
                        )}

                        {/* Regular flow analysis */}
                        <FlowSummaryAnalysis flows={flows} startIndex={0} onLoadSuspiciousReasons={loadSuspiciousFlowReasons} />
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
